import { Theme } from '@eversports/react-components/design-tokens/theme'
import CircularProgress from '@eversports/react-components/primitives/CircularProgress'
import { VerticalFlex } from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import Placeholder from '@eversports/react-components/primitives/Placeholder'
import Text from '@eversports/react-components/primitives/Text'
import { makeStyles } from '@mui/styles'
import PyroForm from 'pyro-form'
import React, { useMemo } from 'react'

import { Localized } from '../../localization/react'
import {
  isStoredBankAccount,
  isStoredCreditCard,
  Mandate,
  PaymentMethod,
  PaymentMethodType,
  PaymentMethodVerificationMethod,
  SelectedPaymentMethod,
  StoredPaymentMethod,
} from '../../typings/Payment'
import BackButton from '../BackButton'

import AdditionalPaymentMethods from './AdditionalPaymentMethods'
import CreditCardPayment from './CreditCardPayment'
import IdealPaymentMethod from './IdealPaymentMethod'

interface Props {
  storedPaymentMethods: Array<StoredPaymentMethod>
  venueMandate: Mandate | null
  paymentMethods: Array<PaymentMethod>
  selectedPaymentMethod: SelectedPaymentMethod
  isPaymentProcessing: boolean
  isOrderConfirmed: boolean
  verifyWith: PaymentMethodVerificationMethod | null
  onSelectedPaymentMethodChange: (updatedSelectedPaymentMethod: SelectedPaymentMethod) => void
}

const useStyles = makeStyles((theme: Theme) => ({
  overlay: {
    position: 'absolute',
    zIndex: 100,
    height: '100%',
    width: '100%',
  },
  paymentFormWrapper: ({ isPaymentProcessing }: Pick<Props, 'isPaymentProcessing'>) => ({
    display: isPaymentProcessing ? 'none' : 'block',
  }),
  container: {
    position: 'relative',
    marginBottom: theme.layoutSpacing.hero,
  },
}))

const PaymentForm = ({
  storedPaymentMethods,
  venueMandate,
  paymentMethods,
  selectedPaymentMethod,
  isPaymentProcessing,
  isOrderConfirmed,
  verifyWith,
  onSelectedPaymentMethodChange,
}: Props) => {
  const classes = useStyles({ isPaymentProcessing })

  const hasCreditCardPayment = useMemo(
    () => paymentMethods.some(({ type }) => type === PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD),
    [paymentMethods],
  )

  const hasIdealPayment = useMemo(
    () => paymentMethods.some(({ type }) => type === PaymentMethodType.PAYMENT_METHOD_IDEAL),
    [paymentMethods],
  )

  const hasSepaIdealPayment = useMemo(
    () =>
      paymentMethods.some(({ type }) => type === PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE) &&
      verifyWith === PaymentMethodVerificationMethod.PAYMENT_METHOD_IDEAL &&
      !venueMandate,
    [paymentMethods],
  )

  const additionalPaymentMethods = useMemo(() => {
    const paymentMethodsWithoutCreditCard = paymentMethods.filter(
      ({ type }) => type !== PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD,
    )

    const paymentMethodsWithoutSepaIdeal = hasSepaIdealPayment
      ? paymentMethodsWithoutCreditCard.filter(({ type }) => type !== PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE)
      : paymentMethodsWithoutCreditCard

    const filteredPaymentMethods = hasIdealPayment
      ? paymentMethodsWithoutSepaIdeal.filter(({ type }) => type !== PaymentMethodType.PAYMENT_METHOD_IDEAL)
      : paymentMethodsWithoutSepaIdeal

    return filteredPaymentMethods
  }, [paymentMethods])

  const storedCreditCards = useMemo(() => storedPaymentMethods.filter(isStoredCreditCard), [storedPaymentMethods])
  const storedBankAccounts = useMemo(() => storedPaymentMethods.filter(isStoredBankAccount), [storedPaymentMethods])

  const handleChange = (values: SelectedPaymentMethod): void => {
    onSelectedPaymentMethodChange(values)
  }

  return (
    <div className={classes.container} data-testid="payment-options-container">
      {isPaymentProcessing && (
        <div className={classes.overlay}>
          <Placeholder height={200} width="100%" center>
            <VerticalFlex verticalSpacing="default" alignItems="center">
              <CircularProgress />
              <Text data-testid="payment-options-payment-processing">
                <Localized id="processing-payment" />
              </Text>
            </VerticalFlex>
          </Placeholder>
        </div>
      )}
      <div className={classes.paymentFormWrapper}>
        <VerticalFlex verticalSpacing="wide">
          <Heading is="h2" textTransform="uppercase">
            <Localized id="payment-heading" />
          </Heading>
          <PyroForm initialValues={selectedPaymentMethod} onChange={handleChange}>
            <VerticalFlex verticalSpacing="default">
              {(hasIdealPayment || hasSepaIdealPayment) && <IdealPaymentMethod isSepaIdeal={hasSepaIdealPayment} />}
              {hasCreditCardPayment && <CreditCardPayment storedCreditCards={storedCreditCards} />}
              {additionalPaymentMethods.length > 0 && (
                <AdditionalPaymentMethods
                  paymentMethods={additionalPaymentMethods}
                  storedBankAccounts={storedBankAccounts}
                  venueMandate={venueMandate}
                  verifyWith={verifyWith}
                />
              )}
            </VerticalFlex>
          </PyroForm>
          {!isOrderConfirmed && <BackButton abortOrderOnBackClick />}
        </VerticalFlex>
      </div>
    </div>
  )
}

export default PaymentForm
